import { useUiLanguage } from "@/i18n/useUiLanguage";
import { systemText, tr } from "@/i18n/copy";
import { useEffect, useRef, useState } from "react";
import { errMsg } from "@/lib/api";
import { Camera, ImagePlus, Loader2, RotateCcw, X } from "lucide-react";

const ID_RATIO = 85.6 / 53.98;

function dataUrlToFile(dataUrl, name) {
  const [header, body] = dataUrl.split(",");
  const mime = header.match(/data:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: mime });
}

export default function GuidedDocumentScanner({ title, onUse, onClose }) {
  useUiLanguage();
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const requestRef = useRef(0), timeoutRef = useRef(null);
  const [ready, setReady] = useState(false);
  const mountedRef = useRef(true);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [uploading, setUploading] = useState(false);
  const uploadPending = useRef(false);

  const stop = () => {
    requestRef.current += 1;
    window.clearTimeout(timeoutRef.current);
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    if (mountedRef.current) setReady(false);
  };
  const markReady = () => {
    if (!streamRef.current || !videoRef.current?.videoWidth) return;
    window.clearTimeout(timeoutRef.current); setReady(true); setBusy(false);
  };
  const startCamera = async () => {
    stop(); setBusy(true); setError("");
    const request = requestRef.current;
    const current = () => mountedRef.current && request === requestRef.current;
    const fail = message => { stop(); if (mountedRef.current) { setBusy(false); setError(tr(message)); } };
    if (!navigator.mediaDevices?.getUserMedia) {
      fail("Anteprima fotocamera non disponibile. Usa Scegli foto."); return;
    }
    timeoutRef.current = window.setTimeout(() => {
      if (current()) fail("La fotocamera non risponde. Premi Riattiva fotocamera oppure Scegli foto.");
    }, 15000);
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false,
        });
      } catch (failure) {
        if (!current()) return;
        if (failure.name !== "OverconstrainedError") throw failure;
        stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: "environment"}, audio: false});
      }
      if (!current()) { stream.getTracks().forEach(track => track.stop()); return; }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) { fail("Anteprima non disponibile. Riapri la fotocamera."); return; }
      video.muted = true; video.playsInline = true; video.srcObject = stream;
      await video.play();
      if (current()) markReady();
    } catch (failure) {
      if (!current()) return;
      fail(failure.name === "NotAllowedError" || failure.name === "SecurityError"
        ? "Accesso alla fotocamera non consentito. Verifica il permesso del sito oppure usa Scegli foto."
        : "Fotocamera non disponibile o già in uso. Premi Riattiva fotocamera oppure Scegli foto.");
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    startCamera();
    const hidden = () => {
      if (document.visibilityState === "hidden") { stop(); setBusy(false); }
    };
    document.addEventListener("visibilitychange", hidden);
    return () => { mountedRef.current = false; stop(); document.removeEventListener("visibilitychange", hidden); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = () => {
    const video = videoRef.current;
    const frame = frameRef.current;
    if (!ready || !video?.videoWidth || !frame) return;
    const videoRect = video.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();
    const scale = Math.max(videoRect.width / video.videoWidth, videoRect.height / video.videoHeight);
    const renderedWidth = video.videoWidth * scale;
    const renderedHeight = video.videoHeight * scale;
    const hiddenX = (renderedWidth - videoRect.width) / 2;
    const hiddenY = (renderedHeight - videoRect.height) / 2;
    let sx = (frameRect.left - videoRect.left + hiddenX) / scale;
    let sy = (frameRect.top - videoRect.top + hiddenY) / scale;
    let sw = frameRect.width / scale;
    let sh = frameRect.height / scale;
    const marginX = sw * 0.045;
    const marginY = sh * 0.07;
    sx = Math.max(0, sx - marginX);
    sy = Math.max(0, sy - marginY);
    sw = Math.min(video.videoWidth - sx, sw + marginX * 2);
    sh = Math.min(video.videoHeight - sy, sh + marginY * 2);
    const outWidth = Math.min(2000, Math.round(sw));
    const outHeight = Math.round(outWidth * (sh / sw));
    const canvas = document.createElement("canvas");
    canvas.width = outWidth;
    canvas.height = outHeight;
    canvas.getContext("2d").drawImage(video, sx, sy, sw, sh, 0, 0, outWidth, outHeight);
    setPreview(canvas.toDataURL("image/jpeg", 0.92));
    stop();
  };

  const retry = async () => { setPreview(""); await startCamera(); };
  const upload = async file => {
    if (!file || uploadPending.current) return;
    uploadPending.current = true; setUploading(true); setError("");
    try { await onUse(file); }
    catch (failure) { if (mountedRef.current) setError(errMsg(failure)); }
    finally { uploadPending.current = false; if (mountedRef.current) setUploading(false); }
  };
  const usePreview = () => upload(dataUrlToFile(preview, `document-${Date.now()}.jpg`));
  const choose = (event) => { const file = event.target.files?.[0]; event.target.value = ""; return upload(file); };
  const pick = ref => { stop(); setBusy(false); ref.current?.click(); };

  return <div className="fixed inset-0 z-[100] bg-black text-white" role="dialog" aria-modal="true" aria-label={title}>
    <div className="relative mx-auto flex h-full max-w-2xl flex-col">
      <header className="flex items-center justify-between gap-3 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))]"><div><p className="text-xs uppercase tracking-[0.2em] text-white/60">{tr("Scansione documento")}</p><h2 className="mt-1 text-lg font-semibold">{title}</h2></div><div className="flex shrink-0 items-center gap-1">{!preview && !ready && !busy && <button type="button" disabled={uploading} onClick={startCamera} className="grid h-11 w-11 place-items-center rounded-full text-white/70 disabled:opacity-40" aria-label={tr("Riattiva fotocamera")} title={tr("Riattiva fotocamera")}><RotateCcw className="h-5 w-5" /></button>}<button disabled={uploading} onClick={() => { stop(); onClose(); }} className="grid h-11 w-11 place-items-center rounded-full bg-white/10" aria-label={tr("Chiudi")}><X className="h-5 w-5" /></button></div></header>
      <div className="relative min-h-0 flex-1 overflow-hidden bg-black">
        <video ref={videoRef} hidden={Boolean(preview)} autoPlay muted playsInline onLoadedData={markReady} onPlaying={markReady} className="h-full w-full object-cover" />
        {preview ? <img src={preview} alt={tr("Anteprima scansione")} className="h-full w-full object-contain" /> : <>
          <div className="pointer-events-none absolute inset-0 bg-black/30" />
          <div ref={frameRef} className="pointer-events-none absolute left-1/2 top-1/2 w-[88%] max-w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.38)]" style={{ aspectRatio: ID_RATIO }}>
            <span className="absolute -left-0.5 -top-0.5 h-8 w-8 rounded-tl-2xl border-l-4 border-t-4 border-primary" /><span className="absolute -right-0.5 -top-0.5 h-8 w-8 rounded-tr-2xl border-r-4 border-t-4 border-primary" /><span className="absolute -bottom-0.5 -left-0.5 h-8 w-8 rounded-bl-2xl border-b-4 border-l-4 border-primary" /><span className="absolute -bottom-0.5 -right-0.5 h-8 w-8 rounded-br-2xl border-b-4 border-r-4 border-primary" />
          </div>
          <p className="pointer-events-none absolute bottom-8 left-4 right-4 text-center text-sm font-medium drop-shadow">{tr("Posiziona il documento dentro il riquadro. Lascia un piccolo margine intorno ai bordi.")}</p>
        </>}
        {busy && <div className="absolute inset-0 grid place-items-center bg-black/50"><Loader2 className="h-8 w-8 animate-spin" /></div>}
      </div>
      {error && <p role="alert" className="mx-4 mt-3 rounded-xl bg-amber-500/15 p-3 text-sm text-amber-100">{systemText(error)}</p>}
      <footer className="grid grid-cols-2 gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
        {preview ? <><button disabled={uploading} onClick={retry} className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20"><RotateCcw className="h-4 w-4" />{tr("Riprova")}</button><button disabled={uploading} onClick={usePreview} className="min-h-12 rounded-xl bg-primary px-4 font-semibold">{uploading ? tr("Caricamento…") : tr("Usa scansione")}</button></> : <><button disabled={uploading} onClick={() => pick(fileRef)} className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20"><ImagePlus className="h-4 w-4" />{tr("Scegli foto")}</button><button disabled={busy || uploading || !ready} onClick={capture} className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-4 font-semibold disabled:opacity-40"><Camera className="h-4 w-4" />{tr("Acquisisci")}</button></>}
        <input ref={fileRef} type="file" disabled={uploading} accept="image/*" className="hidden" onChange={choose} />
      </footer>
    </div>
  </div>;
}
